import React, { useCallback, useEffect, useRef } from "react";
import { CloseIcon, SearchIcon } from "../icons";
import {
  SEARCH_RESULTS_BASE_URL,
  buildSearchResultsUrl
} from "./searchOverlayConstants";
import { highlightMatch } from "./searchOverlayUtils";

const FOCUSABLE_SELECTOR =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Global search overlay: full-screen drawer on mobile, centred modal on desktop (DJSIRSBVUU-18).
 */
const SearchOverlay = ({
  open,
  onClose,
  searchTerm,
  onSearchTermChange,
  suggestions
}) => {
  const inputRef = useRef(null);
  const dialogRef = useRef(null);
  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  const handleFormSubmit = event => {
    if (!searchTerm.trim()) {
      event.preventDefault();
    }
  };

  const handleClearInput = () => {
    onSearchTermChange({ target: { value: "" } });
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleKeyDown = useCallback(event => {
    if (event.key !== "Tab" || !dialogRef.current) {
      return;
    }

    const focusables = Array.from(
      dialogRef.current.querySelectorAll(FOCUSABLE_SELECTOR)
    ).filter(
      el =>
        !el.hasAttribute("disabled") &&
        el.getAttribute("aria-hidden") !== "true" &&
        window.getComputedStyle(el).display !== "none"
    );

    if (focusables.length === 0) {
      return;
    }

    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement;

    if (event.shiftKey) {
      if (active === first || !dialogRef.current.contains(active)) {
        event.preventDefault();
        last.focus();
      }
    } else if (active === last) {
      event.preventDefault();
      first.focus();
    }
  }, []);

  if (!open) {
    return null;
  }

  const hasSuggestions = Array.isArray(suggestions) && suggestions.length > 0;
  const showClearInput = searchTerm.length > 0;

  return (
    <div
      className="bv-search-overlay"
      id="bv-global-search"
      role="dialog"
      aria-modal="true"
      aria-labelledby="bv-search-overlay-title"
      ref={dialogRef}
      onKeyDown={handleKeyDown}
    >
      <button
        type="button"
        className="bv-search-overlay__backdrop"
        aria-label="Close search"
        onClick={onClose}
      />
      <div className="bv-search-overlay__panel">
        <div className="bv-search-overlay__card">
          <div className="bv-search-overlay__header">
            <h2
              id="bv-search-overlay-title"
              className="bv-search-overlay__title"
            >
              Search
            </h2>
            <button
              type="button"
              className="bv-search-overlay__close"
              onClick={onClose}
            >
              <span>Close</span>
              <img
                alt=""
                src={require("../../../images/search-overlay-close.svg")}
              />
            </button>
          </div>

          <form
            className="bv-search-overlay__form"
            action={SEARCH_RESULTS_BASE_URL}
            method="get"
            onSubmit={handleFormSubmit}
            autoComplete="off"
          >
            <div
              className={`bv-search-overlay__field ${
                showClearInput ? "bv-search-overlay__field--has-clear" : ""
              }`}
            >
              <label className="sr-only" htmlFor="bv-search-input">
                Search this website
              </label>
              <input
                id="bv-search-input"
                className="bv-search-overlay__input"
                name="query"
                type="search"
                placeholder="I am looking for..."
                value={searchTerm}
                onChange={onSearchTermChange}
                ref={inputRef}
                enterkeyhint="search"
              />
              {showClearInput ? (
                <button
                  type="button"
                  className="bv-search-overlay__clear"
                  aria-label="Clear search"
                  onClick={handleClearInput}
                >
                  <CloseIcon size={20} />
                </button>
              ) : null}
              <button
                type="submit"
                className="bv-search-overlay__submit"
                aria-label="Search"
              >
                <SearchIcon size={32} />
              </button>
            </div>

            <div
              className="bv-search-overlay__results"
              aria-live="polite"
              aria-atomic="true"
            >
              {hasSuggestions ? (
                <ul className="bv-search-overlay__suggestions">
                  {suggestions.map(item => (
                    <li key={item.disp}>
                      <a
                        className="bv-search-overlay__suggestion"
                        href={buildSearchResultsUrl(item.disp)}
                      >
                        <span>{highlightMatch(item.disp, searchTerm)}</span>
                        <img
                          className="bv-search-overlay__chevron"
                          alt=""
                          src={require("../../../images/search-overlay-chevron.svg")}
                        />
                      </a>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="bv-search-overlay__help">
                  Start typing to see suggestions.
                </p>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SearchOverlay;
